import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { gradeService, Grade, GradeHorario, agendaProfessorService, apiServices, professorService, Professor, DiagnosticoItem, SugestaoRemanejamento } from '../services/api';
import LoadingModal from '../components/LoadingModal';
import ConfirmationModal from '../components/ConfirmationModal';
import UnsavedChangesModal from '../components/UnsavedChangesModal';
import AlertModal from '../components/AlertModal';
import SubstituirProfessorModal from '../components/SubstituirProfessorModal';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import '../components/GradeGrid.css';

// Estilo para cards com alocação inválida (professor sem vínculo com turma) e cards bloqueados
const invalidAllocationStyle = `
  .invalid-allocation {
    background: linear-gradient(45deg, #6c757d, #adb5bd) !important;
    animation: blinkGray 0.8s infinite alternate !important;
    border: 2px solid #495057 !important;
  }
  
  .blocked-card {
    border: 3px solid #dc3545 !important;
    box-shadow: 0 0 10px rgba(220, 53, 69, 0.3) !important;
    position: relative;
  }
  
  .blocked-indicator {
    position: absolute;
    top: 5px;
    right: 5px;
    z-index: 10;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  
  @keyframes blinkGray {
    0% { background: linear-gradient(45deg, #6c757d, #adb5bd); opacity: 0.7; }
    100% { background: linear-gradient(45deg, #495057, #6c757d); opacity: 1; }
  }

  .blocked-move-feedback {
    border: 3px solid #dc3545 !important;
    animation: shakeAndBlink 0.6s ease-in-out !important;
    position: relative;
  }

  .blocked-move-feedback::after {
    content: '🚫';
    position: absolute;
    top: 5px;
    right: 5px;
    z-index: 10;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  @keyframes shakeAndBlink {
    0%, 100% { transform: translateX(0); background-color: inherit; }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-3px); background-color: rgba(220, 53, 69, 0.1); }
    20%, 40%, 60%, 80% { transform: translateX(3px); background-color: rgba(220, 53, 69, 0.2); }
  }
`;

// Injetar o estilo no documento
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = invalidAllocationStyle;
  document.head.appendChild(styleElement);
}

// Disciplina pode ter 1 ou 2 professores (coautoria) — helpers para exibir/comparar os dois
// de forma consistente em toda a tela, sem precisar duplicar essa lógica em cada lugar.
function professorIdsDaDisciplina(disciplina?: GradeHorario['disciplina'] | null): string[] {
  if (!disciplina) return [];
  return [disciplina.professor?.id, disciplina.professor2?.id].filter((id): id is string => !!id);
}

function professorNomeDaDisciplina(disciplina?: GradeHorario['disciplina'] | null): string {
  if (!disciplina) return 'Não atribuído';
  const nomes = [disciplina.professor?.nome, disciplina.professor2?.nome].filter((nome): nome is string => !!nome);
  return nomes.length > 0 ? nomes.join(' e ') : 'Não atribuído';
}



interface CellData {
  id: string;
  professor: string;
  /** ids dos 1-2 professores desta aula — usado para destacar/comparar (não comparar pelo nome, que pode ser ambíguo entre solo e coautoria) */
  professorIds: string[];
  disciplina: string;
  gradeHorarioId: string;
  turmaId: string;
  horarioKey: string;
  conflitoProfessorAgenda?: boolean;
  conflitoHorario?: boolean;
  bloqueado?: boolean;
  originalData: GradeHorario;
  displayText?: string;
}





const GradeViewPage: React.FC = () => {
  const { gradeId } = useParams<{ gradeId: string }>();
  const navigate = useNavigate();
  const [grade, setGrade] = useState<Grade | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gridData, setGridData] = useState<{ [day: number]: CellData[] }>({});
  const [isModified, setIsModified] = useState(false);
  const [showSubstituirProfessorModal, setShowSubstituirProfessorModal] = useState(false);
  const [professores, setProfessores] = useState<Professor[]>([]);
  const [gerandoGradeLaboratorio, setGerandoGradeLaboratorio] = useState(false);
  const [aplicandoRemanejamento, setAplicandoRemanejamento] = useState(false);
  const [resultadoLabModal, setResultadoLabModal] = useState<{
    show: boolean;
    gradeLaboratorioId: string | null;
    diagnostico: DiagnosticoItem[];
    sugestoesRemanejamento: SugestaoRemanejamento[];
    aceitas: Set<string>;
    erro: string | null;
  }>({ show: false, gradeLaboratorioId: null, diagnostico: [], sugestoesRemanejamento: [], aceitas: new Set(), erro: null });


  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [selectedCell, setSelectedCell] = useState<{ day: number; turmaId: string; horarioKey: string } | null>(null);
  const [selectedItem, setSelectedItem] = useState<CellData | null>(null);
  const [draggedItem, setDraggedItem] = useState<CellData | null>(null);
  const [dragOverCell, setDragOverCell] = useState<{ dia: number; turmaId: string; horarioKey: string } | null>(null);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showPrintOptionsModal, setShowPrintOptionsModal] = useState(false);
  const [printConfig, setPrintConfig] = useState({
    displayMode: 'professor', // 'professor', 'disciplina', 'both', 'truncated'
    truncateLength: 'short' // 'full', 'short' (3 letters)
  });

  // Estado para feedback visual de movimentação bloqueada
  const [blockedMoveFeedback, setBlockedMoveFeedback] = useState<string | null>(null);

  // Estado para destacar professor no duplo clique
  const [highlightedProfessor, setHighlightedProfessor] = useState<string | null>(null);
  
  // Estado para controlar cards com alocação inválida (professor sem vínculo com turma)
  const [invalidAllocations, setInvalidAllocations] = useState<Set<string>>(new Set());
  
  // Estado para controlar o modal do manual da grade
  const [showManualModal, setShowManualModal] = useState(false);
  
  // Estado para controlar o modo de grade parcial
  const [isPartialGradeMode, setIsPartialGradeMode] = useState(false);
  const [blockedCards, setBlockedCards] = useState<Set<string>>(new Set());
  const [blockedCardsBackup, setBlockedCardsBackup] = useState<Set<string>>(new Set());
  const [isGeneratingPartialGrade, setIsGeneratingPartialGrade] = useState(false);
  
  // Cache para disciplinas por turma para evitar requests repetidos (usando useRef para evitar re-renders)
  const disciplinasCacheRef = useRef<Map<string, any[]>>(new Map());

  // Função para alternar modo grade parcial com backup/restauração
  const togglePartialGradeMode = () => {
    if (isPartialGradeMode) {
      // Saindo do modo: restaurar backup
      setBlockedCards(new Set(blockedCardsBackup));
      setIsPartialGradeMode(false);
    } else {
      // Entrando no modo: fazer backup
      setBlockedCardsBackup(new Set(blockedCards));
      setIsPartialGradeMode(true);
    }
  };

  // Função para mostrar feedback visual de movimentação bloqueada
  const showBlockedMoveFeedback = (cardId: string) => {
    setBlockedMoveFeedback(cardId);
    setTimeout(() => {
      setBlockedMoveFeedback(null);
    }, 600); // Duração da animação
  };

  // Função para verificar se algum dos professores (solo ou coautoria) tem vínculo com a turma (disciplina cadastrada)
  const verificarVinculoProfessorTurma = async (professorIds: string[], turmaId: string): Promise<boolean> => {
    try {
      // Verificar se já temos as disciplinas no cache
      let disciplinas = disciplinasCacheRef.current.get(turmaId);
      
      if (!disciplinas) {
        // Buscar disciplinas da turma apenas se não estiver no cache
        disciplinas = await apiServices.getDisciplinasByTurma(turmaId);

        // Garantir que disciplinas não seja undefined
        if (!disciplinas) {
          disciplinas = [];
        }

        // Armazenar no cache (sem causar re-render)
        disciplinasCacheRef.current.set(turmaId, disciplinas);
      }
      
      // Verificar se existe alguma disciplina com algum dos professores especificados (solo ou coautoria)
      const temVinculo = disciplinas?.some((disciplina: any) =>
        professorIdsDaDisciplina(disciplina).some((id) => professorIds.includes(id))
      ) || false;
      
      return temVinculo;
    } catch (error) {
      console.error('Erro ao verificar vínculo professor-turma:', error);
      return false; // Em caso de erro, considera como sem vínculo
    }
  };
  
  // Função para verificar vínculos de cards e atualizar estado de alocações inválidas
  const verificarVinculoCards = useCallback(async (cards: CellData[]) => {
    try {
      const resultados = new Map<string, boolean>();
      
      // Processar todos os cards em paralelo para otimizar performance
      const promises = cards.map(async (card) => {
        if (card.professorIds.length > 0) {
          const temVinculo = await verificarVinculoProfessorTurma(card.professorIds, card.turmaId);
          resultados.set(card.id, !temVinculo); // true = inválido, false = válido
        } else {
          resultados.set(card.id, false); // Card sem professor é válido
        }
      });
      
      await Promise.all(promises);
      
      // Atualizar estado uma única vez com todos os resultados
      setInvalidAllocations(prevInvalidAllocations => {
        const newInvalidAllocations = new Set(prevInvalidAllocations);
        
        resultados.forEach((isInvalid, cardId) => {
          if (isInvalid) {
            newInvalidAllocations.add(cardId);
          } else {
            newInvalidAllocations.delete(cardId);
          }
        });
        
        return newInvalidAllocations;
      });
    } catch (error) {
      console.error('Erro ao verificar vínculos dos cards:', error);
    }
  }, []);



  const horarios = [
    '07:30-08:20', '08:20-09:10', '09:10-10:00', '10:15-11:05', '11:05-11:55',
    '13:00-13:50', '13:50-14:40', '14:40-15:30', '15:45-16:35', '16:35-17:25'
  ];

  const handleBackClick = () => {
    if (isModified) {
      setShowUnsavedChangesModal(true);
    } else {
      navigate('/grades');
    }
  };

  // Gera a Grade de Laboratório a partir desta grade já gerada — só as disciplinas com
  // vínculo de laboratório configurado (Turmas e Disciplinas) participam.
  const handleGerarGradeLaboratorio = async () => {
    if (!gradeId) return;
    setGerandoGradeLaboratorio(true);
    try {
      const resultado = await gradeService.gerarGradeLaboratorio(gradeId);
      setResultadoLabModal({
        show: true,
        gradeLaboratorioId: resultado.id,
        diagnostico: resultado.diagnostico,
        sugestoesRemanejamento: resultado.sugestoesRemanejamento,
        aceitas: new Set(),
        erro: null,
      });
    } catch (err: any) {
      setResultadoLabModal({
        show: true,
        gradeLaboratorioId: null,
        diagnostico: [],
        sugestoesRemanejamento: [],
        aceitas: new Set(),
        erro: err.response?.data?.message || 'Erro ao gerar a grade de laboratório.',
      });
    } finally {
      setGerandoGradeLaboratorio(false);
    }
  };

  const handleToggleAceitarSugestao = (gradeHorarioId: string) => {
    setResultadoLabModal(prev => {
      const aceitas = new Set(prev.aceitas);
      if (aceitas.has(gradeHorarioId)) {
        aceitas.delete(gradeHorarioId);
      } else {
        aceitas.add(gradeHorarioId);
      }
      return { ...prev, aceitas };
    });
  };

  // Só aplica as trocas que o usuário marcou explicitamente — nada é salvo na Grade
  // principal sem essa confirmação explícita, mesmo quando a troca é "pronta".
  const handleAplicarRemanejamento = async () => {
    if (!gradeId) return;
    const sugestoesAceitas = resultadoLabModal.sugestoesRemanejamento.filter(
      (s) => s.trocaCom && resultadoLabModal.aceitas.has(s.aulaSemLaboratorio.gradeHorarioId)
    );
    if (sugestoesAceitas.length === 0) return;

    setAplicandoRemanejamento(true);
    try {
      const trocas = sugestoesAceitas.map((s) => ({
        gradeHorarioIdA: s.aulaSemLaboratorio.gradeHorarioId,
        gradeHorarioIdB: s.trocaCom!.gradeHorarioId,
      }));
      const resultado = await gradeService.aplicarRemanejamentoLaboratorio(gradeId, trocas);
      setResultadoLabModal({
        show: true,
        gradeLaboratorioId: resultado.id,
        diagnostico: resultado.diagnostico,
        sugestoesRemanejamento: resultado.sugestoesRemanejamento,
        aceitas: new Set(),
        erro: null,
      });
      setSuccessMessage('Grade atualizada e laboratório recalculado com sucesso!');
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || 'Erro ao aplicar o remanejamento de horário.');
      setTimeout(() => setErrorMessage(null), 5000);
    } finally {
      setAplicandoRemanejamento(false);
    }
  };

  // Função para executar grade parcial
  const handleRunPartialGrade = async () => {
    if (!grade?.id || isGeneratingPartialGrade) return;
    
    setIsGeneratingPartialGrade(true);
    
    try {
      // Chamar API de grade parcial diretamente
      const newGradeId = await gradeService.gerarParcial(grade.id);
      
      // Abrir nova guia do navegador com a nova grade
      const baseUrl = window.location.origin;
      const novaUrl = `${baseUrl}/grades/view/${newGradeId}`;
      window.open(novaUrl, '_blank');
      
      setSuccessMessage('Grade parcial gerada com sucesso! Nova grade aberta em nova guia.');
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (error: any) {
      console.error('Erro ao gerar grade parcial:', error);
      setShowAlert(true);
    } finally {
      setIsGeneratingPartialGrade(false);
    }
  };

  // Função auxiliar para verificar se um card está bloqueado
  const isCardBlocked = (item: CellData | null | undefined) => {
    if (!item) return false;
    
    // Se foi marcado como desbloqueado, não está bloqueado
    if (blockedCards.has(`unblock_${item.id}`)) return false;
    
    // Se está no set local ou veio bloqueado do banco
    return blockedCards.has(item.id) || item.bloqueado;
  };

  // Função para lidar com cliques nos cards no modo grade parcial
  const handleCardClick = (cellData: CellData, event: React.MouseEvent) => {
    if (!isPartialGradeMode) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    const cardId = cellData.id;
    const newBlockedCards = new Set(blockedCards);
    
    // Verificar se o card está bloqueado usando a função auxiliar
    const isCurrentlyBlocked = isCardBlocked(cellData);
    
    if (isCurrentlyBlocked) {
      // Se está bloqueado, desbloquear (remover do set local)
      newBlockedCards.delete(cardId);
      // Se veio bloqueado do banco, sempre adicionar ao set com prefixo para marcar como desbloqueado
      if (cellData.bloqueado) {
        newBlockedCards.add(`unblock_${cardId}`);
      }
    } else {
      // Se não está bloqueado, bloquear
      // Remover possível marcação de desbloqueio
      newBlockedCards.delete(`unblock_${cardId}`);
      newBlockedCards.add(cardId);
    }
    
    setBlockedCards(newBlockedCards);
  };

  // Função para lidar com duplo clique (bloquear todas as aulas do professor)
  const handleCardDoubleClick = (cellData: CellData, event: React.MouseEvent) => {
    if (!isPartialGradeMode) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    const professorIds = cellData.professorIds;
    const newBlockedCards = new Set(blockedCards);

    // Encontrar todos os cards de qualquer um dos professores desta aula (solo ou coautoria)
    const professorCards: string[] = [];
    Object.values(gridData).flat().forEach(item => {
      if (item.professorIds.some(id => professorIds.includes(id))) {
        professorCards.push(item.id);
      }
    });
    
    // Verificar se todos os cards do professor já estão bloqueados
    const allProfessorCardsBlocked = professorCards.every(cardId => {
      const item = Object.values(gridData).flat().find(item => item.id === cardId);
      return item ? isCardBlocked(item) : false;
    });
    
    if (allProfessorCardsBlocked) {
      // Se todos estão bloqueados, desbloquear todos
      professorCards.forEach(cardId => {
        const item = Object.values(gridData).flat().find(item => item.id === cardId);
        newBlockedCards.delete(cardId);
        // Se veio bloqueado do banco, marcar como desbloqueado
        if (item?.bloqueado && !blockedCards.has(cardId)) {
          newBlockedCards.add(`unblock_${cardId}`);
        }
      });
    } else {
      // Se nem todos estão bloqueados, bloquear todos
      professorCards.forEach(cardId => {
        // Remover possível marcação de desbloqueio
        newBlockedCards.delete(`unblock_${cardId}`);
        newBlockedCards.add(cardId);
      });
    }
    
    setBlockedCards(newBlockedCards);
  };

  // Função para clique no nome da turma (toggle de bloqueio de todos os horários do dia)
  const handleTurmaClick = (turmaId: string, dia: number, event: React.MouseEvent) => {
    if (!isPartialGradeMode) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    const newBlockedCards = new Set(blockedCards);
    
    // Encontrar todos os cards da turma neste dia específico
    const turmaCards: string[] = [];
    if (gridData[dia]) {
      gridData[dia].forEach(item => {
        if (item.turmaId === turmaId) {
          turmaCards.push(item.id);
        }
      });
    }
    
    // Verificar se todos os cards da turma já estão bloqueados
    const allTurmaCardsBlocked = turmaCards.every(cardId => {
      const item = gridData[dia]?.find(item => item.id === cardId);
      // Usar a mesma lógica da função isCardBlocked mas considerando o estado atual do newBlockedCards
      if (!item) return false;
      // Se foi marcado como desbloqueado, não está bloqueado
      if (newBlockedCards.has(`unblock_${cardId}`)) return false;
      // Se está no set local ou veio bloqueado do banco
      return newBlockedCards.has(cardId) || item.bloqueado;
    });
    
    if (allTurmaCardsBlocked) {
      // Se todos estão bloqueados, desbloquear todos
      turmaCards.forEach(cardId => {
        const item = gridData[dia]?.find(item => item.id === cardId);
        newBlockedCards.delete(cardId);
        // Se veio bloqueado do banco, sempre marcar como desbloqueado
        if (item?.bloqueado) {
          newBlockedCards.add(`unblock_${cardId}`);
        }
      });
    } else {
      // Se nem todos estão bloqueados, bloquear todos
      turmaCards.forEach(cardId => {
        // Remover possível marcação de desbloqueio
        newBlockedCards.delete(`unblock_${cardId}`);
        newBlockedCards.add(cardId);
      });
    }
    
    setBlockedCards(newBlockedCards);
  };

  const handleConfirmExit = () => {
    setShowUnsavedChangesModal(false);
    navigate('/grades');
  };

  const handleSaveAndExit = async () => {
    setShowUnsavedChangesModal(false);
    try {
      await handleSave();
      navigate('/grades');
    } catch (error) {
      // O erro já é tratado na função handleSave
      // Se houver erro, não navega
    }
  };

  const handlePrintGrade = () => {
    setShowPrintOptionsModal(true);
  };

  const handlePrintOption = (option: string) => {
    setShowPrintOptionsModal(false);
    
    switch (option) {
      case 'grade-completa':
        setShowPrintModal(true);
        break;
      case 'horario-professores':
        executePrintProfessores();
        break;
      case 'horario-turmas':
        executePrintTurmas();
        break;
    }
  };

  const executePrintProfessores = async () => {
    if (!grade) return;
    
    try {
      setSaving(true);

      // Buscar todos os professores únicos da grade
      const professores = new Set<string>();
      Object.values(gridData).forEach(dayData => {
        dayData.forEach(cell => {
          if (cell.professor && cell.professor.trim()) {
            professores.add(cell.professor);
          }
        });
      });
      
      const professorList = Array.from(professores).sort();

      // Criar PDF com horários dos professores
      const pdf = new jsPDF('portrait', 'mm', 'a4');
      let pageCount = 0;
      
      for (const professor of professorList) {
        if (pageCount > 0) {
          pdf.addPage();
        }
        
        // Cabeçalho
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(16);
        pdf.text(`Professor: ${professor}`, 20, 20);
        pdf.setFontSize(12);
        pdf.text(`Grade: ${grade.nome}`, 20, 30);
        
        // Criar tabela de horários para este professor
        createProfessorScheduleTable(pdf, professor, 45);
        
        pageCount++;
      }
      
      // Abrir no navegador ao invés de fazer download
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
      
    } catch (error) {
      console.error('Erro ao gerar PDF dos professores:', error);
      setErrorMessage('Erro ao gerar PDF dos professores');
    } finally {
      setSaving(false);
    }
  };
  
  const executePrintTurmas = async () => {
    if (!grade) return;
    
    try {
      setSaving(true);

      // Buscar turmas únicas da grade atual usando Map para evitar duplicatas
      const turmasMap = new Map();
      grade.horarios?.forEach(h => {
        if (h.turma && !turmasMap.has(h.turma.id)) {
          turmasMap.set(h.turma.id, h.turma);
        }
      });
      const turmas = Array.from(turmasMap.values());

      // Criar PDF com horários das turmas (2 por página)
      const pdf = new jsPDF('portrait', 'mm', 'a4');
      let pageCount = 0;
      
      for (let i = 0; i < turmas.length; i += 2) {
        if (pageCount > 0) {
          pdf.addPage();
        }
        
        // Primeira turma na página
        const turma1 = turmas[i];
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.text(`Turma: ${turma1.nome}`, 20, 20);
        pdf.setFontSize(10);
        pdf.text(`Grade: ${grade.nome}`, 20, 28);
        
        createTurmaScheduleTable(pdf, turma1, 35);
        
        // Segunda turma na página (se existir)
        if (i + 1 < turmas.length) {
          const turma2 = turmas[i + 1];
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(12);
          pdf.text(`Turma: ${turma2.nome}`, 20, 140);
          pdf.setFontSize(10);
          pdf.text(`Grade: ${grade.nome}`, 20, 148);
          
          createTurmaScheduleTable(pdf, turma2, 155);
        }
        
        pageCount++;
      }
      
      // Abrir no navegador ao invés de fazer download
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
      
    } catch (error) {
      console.error('Erro ao gerar PDF das turmas:', error);
      setErrorMessage('Erro ao gerar PDF das turmas');
    } finally {
      setSaving(false);
    }
  };

  const createProfessorScheduleTable = (pdf: jsPDF, professor: string, startY: number) => {
    const diasSemana = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta'];
    
    // Obter TODOS os horários da grade (não apenas do professor)
    const todosHorarios = new Set<string>();
    if (grade?.horarios) {
      grade.horarios.forEach(h => {
        const inicio = h.horario.inicio.substring(0, 5);
        const fim = h.horario.fim.substring(0, 5);
        const horarioKey = `${inicio}-${fim}`;
        todosHorarios.add(horarioKey);
      });
    }
    
    const horariosList = Array.from(todosHorarios).sort();
    
    // Configurações da tabela
    const cellWidth = 30;
    const cellHeight = 10; // Diminuído de 12 para 10
    const startX = 20;
    
    // Cabeçalho
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    
    // Célula de horário
    pdf.rect(startX, startY, cellWidth, cellHeight);
    const horarioTextWidth = pdf.getTextWidth('Horário');
    pdf.text('Horário', startX + (cellWidth - horarioTextWidth) / 2, startY + 7);
    
    // Cabeçalhos dos dias
    diasSemana.forEach((dia, index) => {
      const x = startX + cellWidth + (index * cellWidth);
      pdf.rect(x, startY, cellWidth, cellHeight);
      const diaTextWidth = pdf.getTextWidth(dia);
      pdf.text(dia, x + (cellWidth - diaTextWidth) / 2, startY + 7);
    });
    
    // Linhas de horários
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    
    horariosList.forEach((horarioKey, horarioIndex) => {
      const y = startY + cellHeight + (horarioIndex * cellHeight);
      
      // Coluna do horário
      pdf.rect(startX, y, cellWidth, cellHeight);
      const horarioKeyWidth = pdf.getTextWidth(horarioKey);
      pdf.text(horarioKey, startX + (cellWidth - horarioKeyWidth) / 2, y + 7);
      
      // Colunas dos dias
      diasSemana.forEach((dia, diaIndex) => {
        const x = startX + cellWidth + (diaIndex * cellWidth);
        pdf.rect(x, y, cellWidth, cellHeight);
        
        // Buscar aula do professor neste horário e dia
        const diaSemana = diaIndex + 1; // Segunda=1, Terça=2, etc.
        const dayData = gridData[diaSemana] || [];
        const aula = dayData.find(cell => 
          cell.professor === professor && cell.horarioKey === horarioKey
        );
        
        if (aula) {
          const turmaText = aula.originalData?.turma?.nome || 'Turma';
          const disciplinaText = aula.disciplina.substring(0, 3); // Primeiras 3 letras
          const cellText = `${turmaText}-${disciplinaText}`;
          
          const cellTextWidth = pdf.getTextWidth(cellText);
          pdf.text(cellText, x + (cellWidth - cellTextWidth) / 2, y + 7);
        }
      });
    });
  };
  
  const createTurmaScheduleTable = (pdf: jsPDF, turma: any, startY: number) => {
    const diasSemana = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta'];
    
    // Obter TODOS os horários da grade (não apenas da turma)
    const todosHorarios = new Set<string>();
    if (grade?.horarios) {
      grade.horarios.forEach(h => {
        const inicio = h.horario.inicio.substring(0, 5);
        const fim = h.horario.fim.substring(0, 5);
        const horarioKey = `${inicio}-${fim}`;
        todosHorarios.add(horarioKey);
      });
    }
    
    const horariosList = Array.from(todosHorarios).sort();
    
    // Configurações da tabela - células menores
    const cellWidth = 28;
    const cellHeight = 8;
    const startX = 15;
    
    // Cabeçalho
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    
    // Célula de horário
    pdf.rect(startX, startY, cellWidth, cellHeight);
    const horarioTextWidth = pdf.getTextWidth('Horário');
    pdf.text('Horário', startX + (cellWidth - horarioTextWidth) / 2, startY + 6);
    
    // Cabeçalhos dos dias
    diasSemana.forEach((dia, index) => {
      const x = startX + cellWidth + (index * cellWidth);
      pdf.rect(x, startY, cellWidth, cellHeight);
      const diaTextWidth = pdf.getTextWidth(dia);
      pdf.text(dia, x + (cellWidth - diaTextWidth) / 2, startY + 6);
    });
    
    // Linhas de horários
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(6);
    
    horariosList.forEach((horarioKey, horarioIndex) => {
      const y = startY + cellHeight + (horarioIndex * cellHeight);
      
      // Coluna do horário
      pdf.rect(startX, y, cellWidth, cellHeight);
      const horarioKeyWidth = pdf.getTextWidth(horarioKey);
      pdf.text(horarioKey, startX + (cellWidth - horarioKeyWidth) / 2, y + 6);
      
      // Colunas dos dias
      diasSemana.forEach((dia, diaIndex) => {
        const x = startX + cellWidth + (diaIndex * cellWidth);
        pdf.rect(x, y, cellWidth, cellHeight);
        
        // Buscar aula da turma neste horário e dia
        const diaSemana = diaIndex + 1; // Segunda=1, Terça=2, etc.
        const dayData = gridData[diaSemana] || [];
        const aula = dayData.find(cell => 
          cell.turmaId === turma.id && cell.horarioKey === horarioKey
        );
        
        if (aula) {
          const professorText = aula.professor.length > 8 ? aula.professor.substring(0, 8) : aula.professor;
          const disciplinaText = aula.disciplina.substring(0, 3);
          const cellText = `${professorText}-${disciplinaText}`;
          
          const cellTextWidth = pdf.getTextWidth(cellText);
          pdf.text(cellText, x + (cellWidth - cellTextWidth) / 2, y + 6);
        }
      });
    });
  };

  const executePrint = async () => {
    if (!grade) return;
    setShowPrintModal(false);

    try {
      // Criar um elemento temporário otimizado para A4 paisagem
      const printElement = document.createElement('div');
      printElement.style.position = 'absolute';
      printElement.style.left = '-9999px';
      printElement.style.top = '0';
      printElement.style.width = '287mm'; // A4 landscape width minus margins (0.5cm each side)
      printElement.style.backgroundColor = 'white';
      printElement.style.padding = '5mm';
      printElement.style.fontFamily = 'Arial, sans-serif';
      printElement.style.fontSize = '12px';
      printElement.style.lineHeight = '1.2';

      // Cabeçalho será criado individualmente para cada página

      // Organizar dados da grade por dia
      const diasSemana = [
        { numero: 1, nome: 'Segunda-feira' },
        { numero: 2, nome: 'Terça-feira' },
        { numero: 3, nome: 'Quarta-feira' },
        { numero: 4, nome: 'Quinta-feira' },
        { numero: 5, nome: 'Sexta-feira' }
      ];

      // Coletar todos os horários únicos
       const horariosUnicos = new Set<string>();
       const turmasUnicas = new Map<string, string>();
       const gradeData: { [key: string]: { disciplina: string; professor: string; displayText: string } } = {};

      if (grade.horarios) {
        grade.horarios.forEach(h => {
          const inicio = h.horario.inicio.substring(0, 5);
          const fim = h.horario.fim.substring(0, 5);
          const horarioKey = `${inicio}-${fim}`;
          horariosUnicos.add(horarioKey);
          turmasUnicas.set(h.turma.id, h.turma.nome);
          
          const cellKey = `${h.horario.diaSemana}-${h.turma.id}-${horarioKey}`;
          const disciplinaNome = h.disciplina?.nome || 'N/A';
          const professorNome = h.disciplina?.professor?.nome || 'N/A';
          
          let displayText = '';
          switch (printConfig.displayMode) {
            case 'professor':
              displayText = professorNome.toUpperCase();
              break;
            case 'disciplina':
              displayText = disciplinaNome;
              break;
            case 'both':
              displayText = `${disciplinaNome} - ${professorNome.toUpperCase()}`;
              break;
            case 'truncated':
              const disciplinaDisplay = printConfig.truncateLength === 'short' 
                ? disciplinaNome.substring(0, 3).toUpperCase()
                : disciplinaNome;
              displayText = `${professorNome.toUpperCase()} - ${disciplinaDisplay}`;
              break;
            default:
              displayText = `${disciplinaNome} - ${professorNome.toUpperCase()}`;
          }
          
          gradeData[cellKey] = {
            disciplina: disciplinaNome,
            professor: professorNome.toUpperCase(),
            displayText: displayText
          };
        });
      }

      const horariosArray = Array.from(horariosUnicos).sort();
      const turmasArray = Array.from(turmasUnicas.entries()).sort((a, b) => a[1].localeCompare(b[1]));

      // Dividir turmas em grupos de 16 para quebra de página
      const turmasPerPage = 16;
      const turmasGroups = [];
      for (let i = 0; i < turmasArray.length; i += turmasPerPage) {
        turmasGroups.push(turmasArray.slice(i, i + turmasPerPage));
      }

      // Criar PDF A4 paisagem sem margens para usar 100% da folha
      const pdf = new jsPDF('l', 'mm', 'a4');
      const margin = 0; // Sem margens para usar toda a folha
      
      // Processar cada grupo de turmas como uma página separada
      for (let pageIndex = 0; pageIndex < turmasGroups.length; pageIndex++) {
        const currentTurmasGroup = turmasGroups[pageIndex];
        
        // Adicionar nova página se não for a primeira
        if (pageIndex > 0) {
          pdf.addPage();
        }
        
        let pageHTML = '';

        // Cabeçalho da página (repetir em cada página)
        pageHTML += `
          <div style="margin-bottom: 15px; text-align: left;">
            <div style="font-size: 10pt; font-weight: bold; color: #000; margin-bottom: 2px;">Grade de Horários</div>
            <div style="font-size: 10pt; color: #000; margin-bottom: 2px;">${grade.nome}</div>
            <div style="font-size: 10pt; color: #000;">Data: ${new Date().toLocaleDateString('pt-BR')} - Página ${pageIndex + 1}/${turmasGroups.length}</div>
          </div>
        `;

        // Calcular largura dinâmica baseada no número de turmas
        const numTurmas = currentTurmasGroup.length;
        let columnWidth: string;
        let tableWidth: string;
        let containerWidth: string;
        
        if (numTurmas <= 10) {
          // Para até 10 turmas, usar colunas fixas para melhor aparência
          const fullPageWidth = 1122; // Largura total A4 paisagem
          const horarioColumnWidth = 80; // Coluna de horário
          const turmaColumnWidth = 90; // Coluna fixa para cada turma
          const totalTableWidth = horarioColumnWidth + (numTurmas * turmaColumnWidth);
          
          columnWidth = `${turmaColumnWidth}px`;
          tableWidth = `${totalTableWidth}px`;
          containerWidth = `${fullPageWidth}px`;
        } else {
          // Para mais de 10 turmas, usar toda a largura A4 paisagem
          const fullPageWidth = 1180;
          const horarioColumnWidth = 80;
          const turmaAvailableWidth = fullPageWidth - horarioColumnWidth;
          const turmaColumnWidth = Math.floor(turmaAvailableWidth / numTurmas);
          
          columnWidth = `${turmaColumnWidth}px`;
          tableWidth = `${fullPageWidth}px`;
          containerWidth = `${fullPageWidth}px`;
        }

        // Criar tabela HTML otimizada para usar toda a folha A4
        let tableHTML = `
          <table style="width: ${tableWidth}; border-collapse: collapse; font-size: 14px; margin: 0;">
            <thead>
              <tr style="background-color: #f8f9fa;">
                <th style="border: 2px solid #000; padding: 2px; text-align: center; font-weight: normal; width: 80px;">Horário</th>
        `;
        
        // Cabeçalhos das turmas do grupo atual
        currentTurmasGroup.forEach(([_, turmaNome]) => {
          tableHTML += `<th style="border: 2px solid #000; padding: 2px; text-align: center; font-weight: normal; font-size: 14px; width: ${columnWidth}; background-color: #f8f9fa;">${turmaNome}</th>`;
        });
        
        tableHTML += `</tr></thead><tbody>`;

        // Agrupar por dia da semana
        diasSemana.forEach(dia => {
          // Verificar se há horários para este dia
          const temHorarios = grade.horarios?.some(h => h.horario.diaSemana === dia.numero);
          if (!temHorarios) return;

          // Cabeçalho do dia
          const totalColumns = currentTurmasGroup.length + 1; // +1 para coluna de horário
          tableHTML += `
            <tr style="background-color: #e9ecef;">
              <td colspan="${totalColumns}" style="border: 2px solid #000; padding: 3px; text-align: center; font-weight: normal; font-size: 14px; background-color: #e9ecef;">
                ${dia.nome}
              </td>
            </tr>
          `;

          // Linhas de horários para este dia
          horariosArray.forEach(horario => {
            const temAulaNesteHorario = grade.horarios?.some(h => {
              const inicio = h.horario.inicio.substring(0, 5);
              const fim = h.horario.fim.substring(0, 5);
              return h.horario.diaSemana === dia.numero && `${inicio}-${fim}` === horario;
            });

            if (!temAulaNesteHorario) return;

            tableHTML += `<tr>`;
            tableHTML += `<td style="border: 2px solid #000; padding: 2px; text-align: center; font-weight: normal; font-size: 12px; width: 80px;">${horario.split('-')[0]}</td>`;
            
            currentTurmasGroup.forEach(([turmaId]) => {
              const cellKey = `${dia.numero}-${turmaId}-${horario}`;
              const cellData = gradeData[cellKey];
            
            if (cellData) {
               if (printConfig.displayMode === 'both') {
                 tableHTML += `
                   <td style="border: 2px solid #000; padding: 2px; text-align: center; font-size: 12px; line-height: 1.1; width: ${columnWidth};">
                     <div style="font-weight: normal; color: #000;">${cellData.disciplina}</div>
                     <div style="color: #000; font-size: 10px;">${cellData.professor}</div>
                   </td>
                 `;
               } else {
                 tableHTML += `
                   <td style="border: 2px solid #000; padding: 2px; text-align: center; font-size: 12px; line-height: 1.2; width: ${columnWidth};">
                     <div style="font-weight: normal; color: #000;">${cellData.displayText}</div>
                   </td>
                 `;
               }
             } else {
               tableHTML += `<td style="border: 2px solid #000; padding: 2px; text-align: center; color: #ccc; font-size: 12px; width: ${columnWidth};">-</td>`;
             }
          });
          
          tableHTML += `</tr>`;
        });
      });

        tableHTML += `</tbody></table>`;
        pageHTML += tableHTML;
        
        // Criar elemento temporário para esta página
        const pageContainer = document.createElement('div');
        pageContainer.style.cssText = `
          position: absolute;
          left: -10px;
          top: 0;
          padding: 10px 10px 20px 10px;
          background: white;
          font-family: Arial, sans-serif;
          width: ${containerWidth}px;
          min-height: 794px;
          border-left: 10px solid #000;
          border-right: 10px solid #000;
          border-bottom: 10px solid #000;
          box-sizing: border-box;
        `;
        pageContainer.innerHTML = pageHTML;
        document.body.appendChild(pageContainer);
        
        // Capturar esta página como imagem
        const canvas = await html2canvas(pageContainer, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          width: pageContainer.scrollWidth,
          height: pageContainer.scrollHeight,
          scrollX: 0,
          scrollY: 0
        });
        
        document.body.removeChild(pageContainer);
        
        // Adicionar imagem ao PDF
        const pdfWidth = pdf.internal.pageSize.getWidth() - (margin * 2);
        const pdfHeight = pdf.internal.pageSize.getHeight() - (margin * 2);
        
        // Calcular dimensões da imagem mantendo proporção
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const aspectRatio = imgWidth / imgHeight;
        
        let finalWidth = pdfWidth;
        let finalHeight = finalWidth / aspectRatio;
        
        // Se a altura exceder a página, ajustar pela altura
        if (finalHeight > pdfHeight) {
          finalHeight = pdfHeight;
          finalWidth = finalHeight * aspectRatio;
        }
        
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, margin, finalWidth, finalHeight);
      }
      
      // Finalizar e abrir PDF
       
       // Abrir PDF no navegador em vez de baixar
       const pdfBlob = pdf.output('blob');
       const pdfUrl = URL.createObjectURL(pdfBlob);
       const fileName = `Grade_${grade.nome.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
       
       // Abrir em nova aba
       const newWindow = window.open(pdfUrl, '_blank');
       if (newWindow) {
         newWindow.document.title = fileName;
         // Limpar URL após um tempo para liberar memória
         setTimeout(() => {
           URL.revokeObjectURL(pdfUrl);
         }, 60000); // 1 minuto
       } else {
         // Fallback se popup foi bloqueado
         const link = document.createElement('a');
         link.href = pdfUrl;
         link.download = fileName;
         link.click();
         URL.revokeObjectURL(pdfUrl);
       }
      
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Erro ao gerar PDF. Tente novamente.');
    }
  };





  const loadGrade = useCallback(async (skipLoadingState = false) => {
    try {
      if (!skipLoadingState) {
        setLoading(true);
      }
      const gradeData = await gradeService.getById(gradeId!);
      setGrade(gradeData);
    } catch (error) {
      console.error('Erro ao carregar grade:', error);
    } finally {
      if (!skipLoadingState) {
        setLoading(false);
      }
    }
  }, [gradeId]);

  const initializeGridData = useCallback(() => {
    if (!grade?.horarios) {
      return;
    }

    const newGridData: { [day: number]: CellData[] } = {};

    grade.horarios.forEach((gradeHorario) => {
      const dia = gradeHorario.horario.diaSemana;
      // Converter formato de tempo de HH:MM:SS para HH:MM
      const inicio = gradeHorario.horario.inicio.substring(0, 5);
      const fim = gradeHorario.horario.fim.substring(0, 5);
      const horarioKey = `${inicio}-${fim}`;

      const cellData: CellData = {
        id: `${dia}-${gradeHorario.turma.id}-${horarioKey}`,
        professor: professorNomeDaDisciplina(gradeHorario.disciplina),
        professorIds: professorIdsDaDisciplina(gradeHorario.disciplina),
        disciplina: gradeHorario.disciplina?.nome || 'Sem disciplina',
        gradeHorarioId: gradeHorario.id,
        turmaId: gradeHorario.turma.id,
        horarioKey,
        conflitoProfessorAgenda: gradeHorario.conflitoProfessorAgenda,
        conflitoHorario: gradeHorario.conflitoHorario,
        bloqueado: gradeHorario.bloqueado || false,
        originalData: gradeHorario
      };
      
      if (!newGridData[dia]) {
        newGridData[dia] = [];
      }
      newGridData[dia].push(cellData);
    });

    setGridData(newGridData);
  }, [grade]);

  useEffect(() => {
    if (gradeId) {
      // Limpar cache de disciplinas ao carregar nova grade
      disciplinasCacheRef.current.clear();
      loadGrade();
    }
  }, [gradeId, loadGrade]);

  useEffect(() => {
    if (showSubstituirProfessorModal && professores.length === 0) {
      professorService.getAll().then(setProfessores).catch((err) => console.error('Erro ao carregar professores:', err));
    }
  }, [showSubstituirProfessorModal, professores.length]);

  useEffect(() => {
    if (grade) {
      // Limpar cache de disciplinas quando grade muda
      disciplinasCacheRef.current.clear();

      // Só reinicializar o grid se não houver modificações locais pendentes
      // Isso evita sobrescrever alterações do usuário
      if (!isModified) {
        initializeGridData();
      }
    }
  }, [grade, initializeGridData, isModified]);
  


  const getCellItem = (dia: number, turmaId: string, horarioKey: string): CellData | undefined => {
    return gridData[dia]?.find(item => 
      item.turmaId === turmaId && item.horarioKey === horarioKey
    );
  };

  const getConflictTooltip = (item: CellData | undefined): string => {
    if (!item) return '';
    
    const messages: string[] = [];
    
    if (invalidAllocations.has(item.id)) {
      messages.push('⚠️ Professor não tem vínculo com esta turma (disciplina não cadastrada)');
    }
    
    if (item.conflitoProfessorAgenda) {
      messages.push('O professor está com a agenda bloqueada para esse horário');
    }
    
    if (item.conflitoHorario) {
      messages.push('Conflito de Horario em turmas');
    }
    
    return messages.join(' | ');
  };

  const hasConflicts = (): boolean => {
    return Object.values(gridData).some(dayData => 
      dayData.some(item => item.conflitoProfessorAgenda || item.conflitoHorario)
    );
  };

  const verificarConflitosHorario = (items: CellData[]): void => {
    const newGridData = { ...gridData };
    const professoresAfetados = new Set<string>();

    // Coletar todos os professores afetados (1 ou 2 por aula, em caso de coautoria)
    items.forEach(item => {
      professorIdsDaDisciplina(item.originalData.disciplina).forEach(id => professoresAfetados.add(id));
    });

    // Verificar conflitos para todos os itens na grade
    Object.keys(newGridData).forEach(dayKey => {
      const dayNum = parseInt(dayKey);
      if (newGridData[dayNum]) {
        newGridData[dayNum].forEach((item, index) => {
          const professorIds = professorIdsDaDisciplina(item.originalData.disciplina);
          if (professorIds.length === 0) return;

          const dia = item.originalData.horario.diaSemana;
          const horarioKey = item.horarioKey;

          // Só verificar se algum dos professores desta aula foi afetado pela movimentação
          if (!professorIds.some(id => professoresAfetados.has(id))) return;

          // Verificar se algum dos mesmos professores está em outra turma no mesmo horário do mesmo dia
          let temConflito = false;

          if (newGridData[dia]) {
            newGridData[dia].forEach(otherItem => {
              if (otherItem.id !== item.id &&
                  otherItem.turmaId !== item.turmaId &&
                  otherItem.horarioKey === horarioKey &&
                  professorIdsDaDisciplina(otherItem.originalData.disciplina).some(id => professorIds.includes(id))) {
                temConflito = true;
              }
            });
          }
          
          // Atualizar o conflito no item
          newGridData[dayNum][index] = {
            ...newGridData[dayNum][index],
            conflitoHorario: temConflito,
            originalData: {
              ...newGridData[dayNum][index].originalData,
              conflitoHorario: temConflito
            }
          };
        });
      }
    });
    
    setGridData(newGridData);
  };

  const verificarDisponibilidadeProfessor = async (item: CellData): Promise<void> => {
    try {
      if (item.originalData.disciplina?.professor?.id && item.originalData.horario?.id) {
        const response = await agendaProfessorService.verificarDisponibilidade(
          item.originalData.disciplina.professor.id,
          item.originalData.horario.id
        );

        // Atualizar o conflitoProfessorAgenda baseado na resposta
        const newGridData = { ...gridData };
        const dia = item.originalData.horario.diaSemana;
        const itemIndex = newGridData[dia]?.findIndex(gridItem => gridItem.id === item.id);

        if (itemIndex !== -1 && newGridData[dia]) {
          const conflito = !response.disponivel;

          newGridData[dia][itemIndex] = {
            ...newGridData[dia][itemIndex],
            conflitoProfessorAgenda: conflito,
            originalData: {
              ...newGridData[dia][itemIndex].originalData,
              conflitoProfessorAgenda: conflito
            }
          };
          setGridData(newGridData);
        }
      }
    } catch (error) {
      console.error('Erro ao verificar disponibilidade do professor:', error);
    }
  };

  const handleDragStart = (e: React.DragEvent, item: CellData) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, dia: number, turmaId: string, horarioKey: string) => {
    e.preventDefault();
    
    // Verificar se está tentando arrastar para uma turma diferente
    if (draggedItem && draggedItem.turmaId !== turmaId) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    
    e.dataTransfer.dropEffect = 'move';
    setDragOverCell({ dia, turmaId, horarioKey });
  };

  const handleDragLeave = () => {
    setDragOverCell(null);
  };

  const handleDrop = (e: React.DragEvent, targetDia: number, targetTurmaId: string, targetHorarioKey: string) => {
    e.preventDefault();
    setDragOverCell(null);
    
    if (!draggedItem) return;
    
    // Verificar se está tentando mover para uma turma diferente
    if (draggedItem.turmaId !== targetTurmaId) {
      showBlockedMoveFeedback(draggedItem.id);
      setDraggedItem(null);
      return;
    }
    
    // Verificar se não está soltando na mesma célula
    const sourceDia = Object.keys(gridData).find(day => 
      gridData[parseInt(day)].some(item => item.id === draggedItem.id)
    );
    
    if (sourceDia && 
        parseInt(sourceDia) === targetDia && 
        draggedItem.turmaId === targetTurmaId && 
        draggedItem.horarioKey === targetHorarioKey) {
      setDraggedItem(null);
      return;
    }
    
    // Obter item da célula de destino
    const targetCell = getCellItem(targetDia, targetTurmaId, targetHorarioKey);
    const newGridData = { ...gridData };
    
    if (targetCell && sourceDia) {
      // Swap only disciplines, keeping turma and horario unchanged
      const sourceDay = parseInt(sourceDia);
      
      // Find the items in their current positions BEFORE removing them
      const draggedIndex = newGridData[sourceDay].findIndex(item => item.id === draggedItem.id);
      const targetIndex = newGridData[targetDia].findIndex(item => item.id === targetCell.id);
      
      if (draggedIndex !== -1 && targetIndex !== -1) {
        // Swap only the discipline data
        const updatedDraggedItem: CellData = {
          ...draggedItem,
          professor: professorNomeDaDisciplina(targetCell.originalData.disciplina),
          professorIds: professorIdsDaDisciplina(targetCell.originalData.disciplina),
          disciplina: targetCell.originalData.disciplina?.nome || 'Sem disciplina',
          originalData: {
            ...draggedItem.originalData,
            disciplina: targetCell.originalData.disciplina
          }
        };

        const updatedTargetItem: CellData = {
          ...targetCell,
          professor: professorNomeDaDisciplina(draggedItem.originalData.disciplina),
          professorIds: professorIdsDaDisciplina(draggedItem.originalData.disciplina),
          disciplina: draggedItem.originalData.disciplina?.nome || 'Sem disciplina',
          originalData: {
            ...targetCell.originalData,
            disciplina: draggedItem.originalData.disciplina
          }
        };
        
        // Update the items in their positions
        newGridData[sourceDay][draggedIndex] = updatedDraggedItem;
        newGridData[targetDia][targetIndex] = updatedTargetItem;
        
        setGridData(newGridData);
        
        // Verificar disponibilidade dos professores após o swap
        verificarDisponibilidadeProfessor(updatedDraggedItem);
        verificarDisponibilidadeProfessor(updatedTargetItem);
        
        // Verificar conflitos de horário após o swap
        verificarConflitosHorario([updatedDraggedItem, updatedTargetItem]);
        
        // Verificar vínculos professor-turma após o swap
        verificarVinculoCards([updatedDraggedItem, updatedTargetItem]);
      }
    } else {
      // Move to empty cell - remove from original position and create item with new position
      if (sourceDia) {
        const sourceDay = parseInt(sourceDia);
        newGridData[sourceDay] = newGridData[sourceDay].filter(item => item.id !== draggedItem.id);
      }
      
      const updatedDraggedItem: CellData = {
        ...draggedItem,
        id: `${targetDia}-${targetTurmaId}-${targetHorarioKey}`,
        turmaId: targetTurmaId,
        horarioKey: targetHorarioKey,
        originalData: {
          ...draggedItem.originalData,
          turma: {
            ...draggedItem.originalData.turma,
            id: targetTurmaId
          },
          horario: {
            ...draggedItem.originalData.horario,
            diaSemana: targetDia,
            inicio: targetHorarioKey.split('-')[0],
            fim: targetHorarioKey.split('-')[1]
          }
        }
      };
      
      // Add to new position
      if (!newGridData[targetDia]) {
        newGridData[targetDia] = [];
      }
      newGridData[targetDia].push(updatedDraggedItem);
      
      setGridData(newGridData);
      
      // Verificar disponibilidade do professor após mover
      verificarDisponibilidadeProfessor(updatedDraggedItem);
      
      // Verificar conflitos de horário após mover
      verificarConflitosHorario([updatedDraggedItem]);
      
      // Verificar vínculos professor-turma após mover
      verificarVinculoCards([updatedDraggedItem]);
    }
    
    setIsModified(true);
    setDraggedItem(null);
  };

  const handleCellClick = (dia: number, turmaId: string, horarioKey: string, item?: CellData) => {
    if (selectedItem && !item) {
      // Verificar se está tentando mover para uma turma diferente
      if (selectedItem.turmaId !== turmaId) {
        showBlockedMoveFeedback(selectedItem.id);
        setSelectedItem(null);
        return;
      }
      
      // Move selected item to empty cell
      const newGridData = { ...gridData };
      
      // Remove from current position
      Object.keys(newGridData).forEach(day => {
        const dayNum = parseInt(day);
        newGridData[dayNum] = newGridData[dayNum].filter(gridItem => gridItem.id !== selectedItem.id);
      });
      
      // Create updated item with new position
      const updatedItem: CellData = {
        ...selectedItem,
        turmaId,
        horarioKey,
        id: `${turmaId}-${horarioKey}`,
        originalData: {
          ...selectedItem.originalData,
          turma: {
            ...selectedItem.originalData.turma,
            id: turmaId
          },
          horario: {
            ...selectedItem.originalData.horario,
            diaSemana: dia,
            inicio: horarioKey.split('-')[0],
            fim: horarioKey.split('-')[1]
          }
        }
      };
      
      // Add to new position
      if (!newGridData[dia]) {
        newGridData[dia] = [];
      }
      newGridData[dia].push(updatedItem);
      
      setGridData(newGridData);
      
      // Verificar disponibilidade do professor após mover
      verificarDisponibilidadeProfessor(updatedItem);
      
      // Verificar conflitos de horário após mover
      verificarConflitosHorario([updatedItem]);
      
      // Verificar vínculos professor-turma após mover
      verificarVinculoCards([updatedItem]);
      
      setSelectedItem(null);
      setSelectedCell(null);
      setIsModified(true);
    } else if (selectedItem && item && selectedItem.id !== item.id) {
      // Verificar se está tentando trocar com card de turma diferente
      if (selectedItem.turmaId !== item.turmaId) {
        showBlockedMoveFeedback(selectedItem.id);
        setSelectedItem(null);
        return;
      }
      
      // Swap only disciplines, keeping turma and horario unchanged
      const newGridData = { ...gridData };
      
      // Find current positions
      let selectedDay = -1;
      let selectedIndex = -1;
      let targetDay = -1;
      let targetIndex = -1;
      
      Object.keys(newGridData).forEach(day => {
        const dayNum = parseInt(day);
        const selectedIdx = newGridData[dayNum].findIndex(gridItem => gridItem.id === selectedItem.id);
        const targetIdx = newGridData[dayNum].findIndex(gridItem => gridItem.id === item.id);
        
        if (selectedIdx !== -1) {
          selectedDay = dayNum;
          selectedIndex = selectedIdx;
        }
        if (targetIdx !== -1) {
          targetDay = dayNum;
          targetIndex = targetIdx;
        }
      });
      
      if (selectedDay !== -1 && targetDay !== -1) {
        // Swap only the discipline data, keeping turma and horario unchanged
        const updatedSelectedItem: CellData = {
          ...selectedItem,
          professor: professorNomeDaDisciplina(item.originalData.disciplina),
          professorIds: professorIdsDaDisciplina(item.originalData.disciplina),
          disciplina: item.originalData.disciplina?.nome || 'Sem disciplina',
          originalData: {
            ...selectedItem.originalData,
            disciplina: item.originalData.disciplina
          }
        };

        const updatedTargetItem: CellData = {
          ...item,
          professor: professorNomeDaDisciplina(selectedItem.originalData.disciplina),
          professorIds: professorIdsDaDisciplina(selectedItem.originalData.disciplina),
          disciplina: selectedItem.originalData.disciplina?.nome || 'Sem disciplina',
          originalData: {
            ...item.originalData,
            disciplina: selectedItem.originalData.disciplina
          }
        };
        
        // Update positions
        newGridData[targetDay][targetIndex] = updatedSelectedItem;
        newGridData[selectedDay][selectedIndex] = updatedTargetItem;
        
        setGridData(newGridData);
        
        // Verificar disponibilidade dos professores após o swap
        verificarDisponibilidadeProfessor(updatedSelectedItem);
        verificarDisponibilidadeProfessor(updatedTargetItem);
        
        // Verificar conflitos de horário após o swap
        verificarConflitosHorario([updatedSelectedItem, updatedTargetItem]);
        
        setSelectedItem(null);
        setSelectedCell(null);
        setIsModified(true);
      }
    } else if (item) {
      // Comportamento normal - Select item
      setSelectedItem(item);
      setSelectedCell({ day: dia, turmaId, horarioKey });
    } else {
      // Deselect
      setSelectedItem(null);
      setSelectedCell(null);
    }
  };

  const handleDoubleClick = (item: CellData) => {
    if (!item) {
      return;
    }

    const professorName = item.professor;
    
    // Se clicou no mesmo professor, remove o destaque
    if (highlightedProfessor === professorName) {
      setHighlightedProfessor(null);
    } else {
      // Destaca o novo professor (cards com conflito não serão pintados, mas a funcionalidade é acionada)
      setHighlightedProfessor(professorName);
    }
  };

  const handleSave = async () => {
    if (!isModified) {
      setErrorMessage('Nenhuma alteração foi feita.');
      return;
    }

    // Verificar se há conflitos
    if (hasConflicts()) {
      setShowConfirmationModal(true);
      return;
    }

    // Se não há conflitos, salvar diretamente
    await performSave();
  };

  const performSave = async () => {
    setErrorMessage(null);
    setSuccessMessage(null);
    setSaving(true);
    setShowConfirmationModal(false);

    try {
      // Criar grade atualizada com os novos horários
      const updatedGradeHorarios: GradeHorario[] = [];
      
      Object.keys(gridData).forEach(day => {
        const dayNum = parseInt(day);
        gridData[dayNum].forEach(item => {
          if (item.originalData) {
            updatedGradeHorarios.push(item.originalData);
          }
        });
      });

      if (grade && updatedGradeHorarios.length > 0) {
        const updatedGrade: Grade = {
          ...grade,
          horarios: updatedGradeHorarios
        };
        
        await gradeService.update(grade.id!, updatedGrade);
        setSuccessMessage('Alterações salvas com sucesso!');
        setIsModified(false);
        
        // Recarregar dados e aguardar a inicialização completa do grid
        if (gradeId) {
          await loadGrade(true); // skipLoadingState = true para evitar sobreposição de modais
          // Aguardar um pequeno delay para garantir que o useEffect do initializeGridData seja executado
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
    } catch (error) {
      console.error('Erro ao salvar alterações:', error);
      setErrorMessage('Erro ao salvar alterações. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmationModal(false);
  };

  // Função para obter todas as turmas únicas da grade ordenadas alfabeticamente
  const getAllTurmasOrdenadas = () => {
    if (!grade?.horarios) return [];
    
    // Criar um mapa de turmas únicas
    const turmasMap = new Map();
    grade.horarios.forEach(h => {
      if (!turmasMap.has(h.turma.id)) {
        turmasMap.set(h.turma.id, h.turma);
      }
    });
    
    // Converter para array e ordenar
    const turmasArray = Array.from(turmasMap.values());

    // Ordenação alfabética simples por nome
    const turmasOrdenadas = turmasArray.sort((a, b) => {
      const aName = a.nome.toLowerCase();
      const bName = b.nome.toLowerCase();
      if (aName < bName) return -1;
      if (aName > bName) return 1;
      return 0;
    });

    return turmasOrdenadas;
  };

  const renderDayGrid = (dia: number) => {
    const diasSemana = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta'];

    // Obter todas as turmas ordenadas globalmente
    const todasTurmasOrdenadas = getAllTurmasOrdenadas();

    // Filtrar apenas as turmas que têm aulas neste dia
    const turmasNesteDia = new Set(
      grade?.horarios
        ?.filter(h => h.horario.diaSemana === dia)
        .map(h => h.turma.id) || []
    );

    // Usar a ordem global, mas mostrar apenas as turmas que têm aulas neste dia
    const turmasUnicas = todasTurmasOrdenadas.filter(turma =>
      turmasNesteDia.has(turma.id)
    );

    // Obter horários únicos para este dia
    const horariosNoDia = grade?.horarios ? 
      Array.from(new Set(
        grade.horarios
          .filter(h => h.horario.diaSemana === dia)
          .map(h => `${h.horario.inicio.substring(0, 5)}-${h.horario.fim.substring(0, 5)}`)
      )).sort() : [];
    
    if (turmasUnicas.length === 0 || horariosNoDia.length === 0) {
      return (
        <div key={dia} className="day-section">
          <div className="day-title">
            <h4>{diasSemana[dia - 1]}</h4>
          </div>
          <div className="no-data">Nenhuma aula programada para este dia</div>
        </div>
      );
    }
    
    return (
      <div key={dia} className="day-section">
        <div className="day-title">
          <h4>{diasSemana[dia - 1]}</h4>
        </div>
        
        <div className="day-grid">
          {/* Cabeçalho com turmas */}
          <div className="day-header-row">
            <div className="time-header">Horário</div>
            {turmasUnicas.map(turma => (
              <div 
                key={turma.id} 
                className="turma-header-cell"
                onClick={(e) => handleTurmaClick(turma.id, dia, e)}
                style={{ cursor: isPartialGradeMode ? 'pointer' : 'default' }}
                title={isPartialGradeMode ? 'Clique para bloquear todos os horários desta turma no dia' : ''}
              >
                <strong>{turma.nome}</strong>
              </div>
            ))}
            {/* Coluna fantasma */}
            <div className="turma-header-cell phantom-column"></div>
          </div>
          
          {/* Linhas de horários */}
          {horariosNoDia.map(horarioKey => {
            const [inicio, fim] = horarioKey.split('-');
            return (
              <div key={horarioKey} className="time-row">
                <div className="time-cell">
                  <div><strong>{inicio}</strong></div>
                  <div><small>{fim}</small></div>
                </div>
                {turmasUnicas.map(turma => {
                  const item = getCellItem(dia, turma.id, horarioKey);
                  const isSelected = selectedCell?.day === dia && 
                                   selectedCell?.turmaId === turma.id && 
                                   selectedCell?.horarioKey === horarioKey;
                  const isTargetHighlight = selectedItem && !item;
                  
                  return (
                    <div
                      key={`${turma.id}-${horarioKey}`}
                      className={`grid-cell ${
                        item ? 'occupied' : 'empty'
                      } ${
                        isSelected ? 'selected' : ''
                      } ${
                        isTargetHighlight ? 'target-highlight' : ''
                      } ${
                        item?.conflitoProfessorAgenda && item?.conflitoHorario ? 'both-conflicts' : 
                        item?.conflitoProfessorAgenda ? 'professor-agenda-conflict' : 
                        item?.conflitoHorario ? 'schedule-conflict' : ''
                      } ${
                        dragOverCell?.dia === dia && 
                        dragOverCell?.turmaId === turma.id && 
                        dragOverCell?.horarioKey === horarioKey &&
                        (!draggedItem || draggedItem.turmaId === turma.id) ? 'drag-over' : ''
                      } ${
                        highlightedProfessor && 
                        item?.professor === highlightedProfessor && 
                        !item?.conflitoProfessorAgenda && 
                        !item?.conflitoHorario ? 'professor-highlighted' : ''
                      } ${
                        item && invalidAllocations.has(item.id) ? 'invalid-allocation' : ''
                      } ${
                        isCardBlocked(item) && isPartialGradeMode ? 'blocked-card' : ''
                      } ${
                        item && blockedMoveFeedback === item.id ? 'blocked-move-feedback' : ''
                      }`}
                      title={isPartialGradeMode && item ? 
                        `Clique para ${isCardBlocked(item) ? 'desbloquear' : 'bloquear'} este card. Duplo clique para bloquear todas as aulas do professor ${item.professor}` :
                        getConflictTooltip(item)
                      }
                      draggable={!isPartialGradeMode && !!item}
                      onDragStart={!isPartialGradeMode && item ? (e) => handleDragStart(e, item) : undefined}
                      onClick={isPartialGradeMode && item ? 
                        (e) => handleCardClick(item, e) : 
                        () => handleCellClick(dia, turma.id, horarioKey)
                      }
                      onDoubleClick={isPartialGradeMode && item ? 
                        (e) => handleCardDoubleClick(item, e) : 
                        () => item && handleDoubleClick(item)
                      }
                      onDragOver={!isPartialGradeMode ? (e) => handleDragOver(e, dia, turma.id, horarioKey) : undefined}
                      onDragLeave={!isPartialGradeMode ? handleDragLeave : undefined}
                      onDrop={!isPartialGradeMode ? (e) => handleDrop(e, dia, turma.id, horarioKey) : undefined}
                      style={!isPartialGradeMode && item ? { cursor: 'move' } : isPartialGradeMode && item ? { cursor: 'pointer' } : {}}
                    >
                      {item ? (
                         <div className="cell-content">

                           <div className="cell-professor">{item.professor || 'Professor não informado'}</div>
                           <div className="cell-disciplina">{item.disciplina || 'Disciplina não informada'}</div>
                           {isPartialGradeMode && isCardBlocked(item) && (
                             <div className="blocked-indicator"><i className="fas fa-lock text-danger"></i></div>
                           )}
                         </div>
                       ) : (
                         <div className="cell-empty">Livre</div>
                       )}
                    </div>
                  );
                })}
                {/* Coluna fantasma */}
                <div className="grid-cell phantom-column"></div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };



  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
            <p className="mt-3 text-muted">Carregando grade...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!grade) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Grade não encontrada.
        </div>
        <button className="btn btn-secondary" onClick={handleBackClick}>
          <i className="fas fa-arrow-left me-2"></i>
          Voltar para Grades
        </button>
      </div>
    );
  }

  return (
    <div className="grade-viewer">
      <div className="grade-header">
        <div>
          <h2 className="d-flex align-items-center">
            <i className="fas fa-calendar-alt me-2"></i>
            Grade de Horários
            {isModified && (
               <span className="badge bg-warning text-dark ms-3 d-flex align-items-center" style={{fontSize: '0.7rem'}} title="Não se esqueça de salvar as alterações na grade">
                 <i className="fas fa-edit me-1"></i>
                 Lembre-se de salvar
               </span>
             )}
          </h2>
          {grade && (
            <p className="grade-info">
              <i className="fas fa-info-circle me-1"></i>
              {grade.nome}
            </p>
          )}
        </div>
        <div className="header-buttons d-flex gap-2">
          <button 
            className="btn btn-outline-info"
            onClick={() => setShowManualModal(true)}
            title="Guia de cores e funcionalidades da grade"
          >
            <i className="fas fa-question-circle me-2"></i>
            Manual da Grade
          </button>
          <button 
            className={`btn ${isPartialGradeMode ? 'btn-warning' : 'btn-outline-warning'}`}
            onClick={togglePartialGradeMode}
            disabled={!isPartialGradeMode && isModified}
            title={isPartialGradeMode ? 
              'Sair do modo de grade parcial' : 
              isModified ? 
                'Salve as alterações antes de entrar no modo de grade parcial' : 
                'Ativar modo de grade parcial para bloquear cards'
            }
          >
            <i className={`fas ${isPartialGradeMode ? 'fa-lock' : 'fa-unlock'} me-2`}></i>
            {isPartialGradeMode ? 'Sair Grade Parcial' : 'Gerar Grade Parcial'}
          </button>
          <button
            className="btn btn-outline-warning"
            onClick={() => setShowSubstituirProfessorModal(true)}
            disabled={isPartialGradeMode || isModified}
            title={isPartialGradeMode || isModified ?
              'Saia do modo de grade parcial e salve as alterações antes de substituir um professor' :
              'Trocar o professor de uma disciplina só nesta grade, sem regenerar'
            }
          >
            <i className="fas fa-people-arrows me-2"></i>
            Substituir Professor
          </button>
          <button
            className="btn btn-outline-primary"
            onClick={handleGerarGradeLaboratorio}
            disabled={isPartialGradeMode || isModified || gerandoGradeLaboratorio}
            title={isPartialGradeMode || isModified ?
              'Saia do modo de grade parcial e salve as alterações antes de gerar a grade de laboratório' :
              'Gerar a grade de laboratório a partir desta grade, para as disciplinas com aulas em laboratório configuradas'
            }
          >
            {gerandoGradeLaboratorio ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Gerando...
              </>
            ) : (
              <>
                <i className="fas fa-flask me-2"></i>
                Gerar Grade de Laboratório
              </>
            )}
          </button>
          <button
            className="btn btn-outline-secondary"
            onClick={handleBackClick}
          >
            <i className="fas fa-arrow-left me-2"></i>
            Voltar
          </button>
          <button 
            className="btn btn-outline-primary"
            onClick={handlePrintGrade}
            disabled={isPartialGradeMode || invalidAllocations.size > 0}
            title={isPartialGradeMode ? 
              "Impressão desabilitada no modo de gerar grade parcial" :
              invalidAllocations.size > 0 ? 
                "Não é possível imprimir: existem professores alocados em turmas sem vínculo (disciplina não cadastrada)" : 
                "Gerar PDF da grade para impressão"
            }
          >
            <i className="fas fa-print me-2"></i>
            Imprimir
            {invalidAllocations.size > 0 && (
              <i className="fas fa-exclamation-triangle text-warning ms-1" title="Alocações inválidas detectadas"></i>
            )}
          </button>
          <button 
            className={`btn ${isPartialGradeMode ? 'btn-primary' : 'btn-success'}`}
            onClick={isPartialGradeMode ? handleRunPartialGrade : handleSave}
            disabled={isPartialGradeMode ? isGeneratingPartialGrade : (!isModified || invalidAllocations.size > 0)}
            title={isPartialGradeMode ? 
              (isGeneratingPartialGrade ? "Gerando grade parcial..." : "Executar geração de grade parcial com cards bloqueados") :
              (invalidAllocations.size > 0 ? 
                "Não é possível salvar: existem professores alocados em turmas sem vínculo (disciplina não cadastrada)" : 
                !isModified ? "Nenhuma alteração foi feita" : "Salvar alterações na grade")
            }
          >
            {isPartialGradeMode && isGeneratingPartialGrade ? (
              <>
                <i className="fas fa-spinner fa-spin me-2"></i>
                Gerando...
              </>
            ) : (
              <>
                <i className={`fas ${isPartialGradeMode ? 'fa-cogs' : 'fa-save'} me-2`}></i>
                {isPartialGradeMode ? 'Rodar Grade Parcial' : 'Salvar Alterações'}
              </>
            )}
            {!isPartialGradeMode && invalidAllocations.size > 0 && (
              <i className="fas fa-exclamation-triangle text-warning ms-1" title="Alocações inválidas detectadas"></i>
            )}
          </button>
        </div>
      </div>

      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="error-message">
          {errorMessage}
        </div>
      )}

      {grade && (
        <div className="grade-grid">
          {/* Cabeçalho com horários */}
          <div className="horarios-header">
            <div className="turma-column-header">Turma / Horário</div>
            {horarios.map(horario => (
              <div key={horario} className="horario-header">
                {horario}
              </div>
            ))}
          </div>
          
          {/* Grid de dados por dia */}
          {[1, 2, 3, 4, 5].map(dia => renderDayGrid(dia))}
        </div>
      )}
      

      

      
      {/* Modal de Confirmação de Conflitos */}
      <ConfirmationModal 
        isOpen={showConfirmationModal}
        title="Conflitos Detectados"
        message="Foram detectados conflitos na grade (conflitos de horário ou professor com agenda bloqueada). Tem certeza que deseja salvar as alterações mesmo assim?"
        onConfirm={performSave}
        onCancel={handleCancelConfirmation}
        confirmText="Continuar"
        cancelText="Cancelar"
      />
      
      {/* Modal de Confirmação de Alterações Não Salvas */}
      <UnsavedChangesModal
        isOpen={showUnsavedChangesModal}
        onClose={() => setShowUnsavedChangesModal(false)}
        onExitWithoutSaving={handleConfirmExit}
        onSaveAndExit={handleSaveAndExit}
        isLoading={saving}
      />

      {/* Modal de Loading para Salvar Alterações */}
      <LoadingModal 
        isOpen={saving}
        title="Salvando Alterações"
        message="Atualizando a lista de horários e pode demorar"
      />

      {/* Modal de Opções de Impressão */}
      {showPrintOptionsModal && (
        <>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title d-flex align-items-center">
                    <i className="fas fa-print me-2"></i>
                    Opções de Impressão
                  </h5>
                  <button 
                    type="button" 
                    className="btn-close btn-close-white" 
                    onClick={() => setShowPrintOptionsModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-4">
                    <h6 className="fw-bold text-primary mb-3">
                      <i className="fas fa-list me-2"></i>
                      Escolha o tipo de impressão:
                    </h6>
                    <div className="row g-3">
                      <div className="col-12">
                        <div className="card border-0 shadow-sm h-100" style={{ cursor: 'pointer' }} onClick={() => handlePrintOption('grade-completa')}>
                          <div className="card-body p-4 text-center">
                            <i className="fas fa-table text-primary mb-3" style={{ fontSize: '2rem' }}></i>
                            <h5 className="card-title text-primary mb-2">Grade Completa</h5>
                            <p className="card-text text-muted">Imprime a grade completa com opções de configuração</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="card border-0 shadow-sm h-100" style={{ cursor: 'pointer' }} onClick={() => handlePrintOption('horario-professores')}>
                          <div className="card-body p-4 text-center">
                            <i className="fas fa-user-tie text-success mb-3" style={{ fontSize: '2rem' }}></i>
                            <h5 className="card-title text-success mb-2">Horário dos Professores</h5>
                            <p className="card-text text-muted">Gera PDF com horário individual de cada professor</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="card border-0 shadow-sm h-100" style={{ cursor: 'pointer' }} onClick={() => handlePrintOption('horario-turmas')}>
                          <div className="card-body p-4 text-center">
                            <i className="fas fa-users text-info mb-3" style={{ fontSize: '2rem' }}></i>
                            <h5 className="card-title text-info mb-2">Horário das Turmas</h5>
                            <p className="card-text text-muted">Gera PDF com horário individual de cada turma</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer bg-light">
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary d-flex align-items-center" 
                    onClick={() => setShowPrintOptionsModal(false)}
                  >
                    <i className="fas fa-times me-2"></i>
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {/* Modal de Configuração de Impressão */}
      {showPrintModal && (
        <>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title d-flex align-items-center">
                    <i className="fas fa-print me-2"></i>
                    Configurações de Impressão
                  </h5>
                  <button 
                    type="button" 
                    className="btn-close btn-close-white" 
                    onClick={() => setShowPrintModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-4">
                    <h6 className="fw-bold text-primary mb-3">
                      <i className="fas fa-cog me-2"></i>
                      Como deseja exibir as informações nas células?
                    </h6>
                    <div className="row g-3">
                      <div className="col-12">
                        <div className="card border-0 shadow-sm">
                          <div className="card-body p-3">
                            <div className="form-check d-flex align-items-center">
                              <input 
                                className="form-check-input me-3" 
                                type="radio" 
                                name="displayMode" 
                                id="professor" 
                                value="professor"
                                checked={printConfig.displayMode === 'professor'}
                                onChange={(e) => setPrintConfig({...printConfig, displayMode: e.target.value})}
                              />
                              <label className="form-check-label d-flex align-items-center" htmlFor="professor">
                                <i className="fas fa-user-tie text-success me-2"></i>
                                <div>
                                  <div className="fw-semibold">Somente Professor</div>
                                  <small className="text-muted">Exibe apenas o nome do professor</small>
                                </div>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="card border-0 shadow-sm">
                          <div className="card-body p-3">
                            <div className="form-check d-flex align-items-center">
                              <input 
                                className="form-check-input me-3" 
                                type="radio" 
                                name="displayMode" 
                                id="disciplina" 
                                value="disciplina"
                                checked={printConfig.displayMode === 'disciplina'}
                                onChange={(e) => setPrintConfig({...printConfig, displayMode: e.target.value})}
                              />
                              <label className="form-check-label d-flex align-items-center" htmlFor="disciplina">
                                <i className="fas fa-book text-info me-2"></i>
                                <div>
                                  <div className="fw-semibold">Somente Disciplina</div>
                                  <small className="text-muted">Exibe apenas o nome da disciplina</small>
                                </div>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="card border-0 shadow-sm">
                          <div className="card-body p-3">
                            <div className="form-check d-flex align-items-center">
                              <input 
                                className="form-check-input me-3" 
                                type="radio" 
                                name="displayMode" 
                                id="both" 
                                value="both"
                                checked={printConfig.displayMode === 'both'}
                                onChange={(e) => setPrintConfig({...printConfig, displayMode: e.target.value})}
                              />
                              <label className="form-check-label d-flex align-items-center" htmlFor="both">
                                <i className="fas fa-layer-group text-warning me-2"></i>
                                <div>
                                  <div className="fw-semibold">Professor e Disciplina</div>
                                  <small className="text-muted">Exibe ambos em duas linhas</small>
                                </div>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="card border-0 shadow-sm">
                          <div className="card-body p-3">
                            <div className="form-check d-flex align-items-center">
                              <input 
                                className="form-check-input me-3" 
                                type="radio" 
                                name="displayMode" 
                                id="truncated" 
                                value="truncated"
                                checked={printConfig.displayMode === 'truncated'}
                                onChange={(e) => setPrintConfig({...printConfig, displayMode: e.target.value})}
                              />
                              <label className="form-check-label d-flex align-items-center" htmlFor="truncated">
                                <i className="fas fa-minus text-primary me-2"></i>
                                <div>
                                  <div className="fw-semibold">Professor e Disciplina - Abreviado</div>
                                  <small className="text-muted">Professor - Disciplina em uma linha</small>
                                </div>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                    

                </div>
                <div className="modal-footer bg-light">
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary d-flex align-items-center" 
                    onClick={() => setShowPrintModal(false)}
                  >
                    <i className="fas fa-times me-2"></i>
                    Cancelar
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-primary d-flex align-items-center" 
                    onClick={executePrint}
                  >
                    <i className="fas fa-print me-2"></i>
                    Imprimir Grade
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {/* Modal do Manual da Grade */}
      {showManualModal && (
        <>
          <div className="modal fade show d-block" tabIndex={-1} style={{zIndex: 1055}}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header bg-info text-white">
                  <h5 className="modal-title d-flex align-items-center">
                    <i className="fas fa-question-circle me-2"></i>
                    Manual da Grade - Guia de Cores e Funcionalidades
                  </h5>
                  <button 
                    type="button" 
                    className="btn-close btn-close-white" 
                    onClick={() => setShowManualModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-12">
                      <h6 className="text-muted mb-3">
                        <i className="fas fa-palette me-2"></i>
                        Entenda o significado de cada cor na grade:
                      </h6>
                    </div>
                    
                    {/* Card Verde - Seleção Normal */}
                    <div className="col-md-6">
                      <div className="card border-success">
                        <div className="card-body">
                          <div className="d-flex align-items-center mb-2">
                            <div 
                              className="me-3" 
                              style={{
                                width: '60px',
                                height: '40px',
                                background: 'linear-gradient(135deg, #28a745, #20c997)',
                                borderRadius: '6px',
                                border: '2px solid #198754',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '10px',
                                fontWeight: 'bold'
                              }}
                            >
                              Prof. A
                            </div>
                            <div>
                              <h6 className="card-title text-success mb-1">
                                 <i className="fas fa-check-circle me-1"></i>
                                 Verde - Seleção
                               </h6>
                               <p className="card-text small mb-0">
                                 De dois clicks sobre um card para exibir todos os horários desse professor
                               </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card Vermelho - Conflito de Horário */}
                    <div className="col-md-6">
                      <div className="card border-danger">
                        <div className="card-body">
                          <div className="d-flex align-items-center mb-2">
                            <div 
                              className="me-3" 
                              style={{
                                width: '60px',
                                height: '40px',
                                background: 'linear-gradient(135deg, #dc3545, #c82333)',
                                borderRadius: '6px',
                                border: '2px solid #bd2130',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '10px',
                                fontWeight: 'bold'
                              }}
                            >
                              Prof. B
                            </div>
                            <div>
                              <h6 className="card-title text-danger mb-1">
                                <i className="fas fa-exclamation-triangle me-1"></i>
                                Vermelho - Conflito
                              </h6>
                              <p className="card-text small mb-0">
                                Professor em mais de uma turma no mesmo horário.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card Amarelo - Conflito de Agenda */}
                    <div className="col-md-6">
                      <div className="card border-warning">
                        <div className="card-body">
                          <div className="d-flex align-items-center mb-2">
                            <div 
                              className="me-3" 
                              style={{
                                width: '60px',
                                height: '40px',
                                background: 'linear-gradient(135deg, #ffc107, #e0a800)',
                                borderRadius: '6px',
                                border: '2px solid #d39e00',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '10px',
                                fontWeight: 'bold'
                              }}
                            >
                              Prof. C
                            </div>
                            <div>
                              <h6 className="card-title text-warning mb-1">
                                <i className="fas fa-clock me-1"></i>
                                Amarelo - Agenda
                              </h6>
                              <p className="card-text small mb-0">
                                Conflito com a agenda pessoal do professor.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card Roxo - Sem Agenda + Conflito */}
                    <div className="col-md-6">
                      <div className="card border-secondary">
                        <div className="card-body">
                          <div className="d-flex align-items-center mb-2">
                            <div 
                              className="me-3" 
                              style={{
                                width: '60px',
                                height: '40px',
                                background: 'linear-gradient(135deg, #6f42c1, #5a32a3)',
                                borderRadius: '6px',
                                border: '2px solid #59359a',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '10px',
                                fontWeight: 'bold'
                              }}
                            >
                              Prof. D
                            </div>
                            <div>
                              <h6 className="card-title text-secondary mb-1">
                                <i className="fas fa-ban me-1"></i>
                                Roxo - Múltiplos Conflitos
                              </h6>
                              <p className="card-text small mb-0">
                                Professor sem agenda disponível e em mais turmas.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card Cinza Piscando - Sem Vínculo */}
                    <div className="col-12">
                      <div className="card border-secondary">
                        <div className="card-body">
                          <div className="d-flex align-items-center mb-2">
                            <div 
                              className="me-3" 
                              style={{
                                width: '60px',
                                height: '40px',
                                background: 'linear-gradient(45deg, #6c757d, #adb5bd)',
                                animation: 'blinkGray 0.8s infinite alternate',
                                borderRadius: '6px',
                                border: '2px solid #495057',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '10px',
                                fontWeight: 'bold'
                              }}
                            >
                              Prof. E
                            </div>
                            <div>
                              <h6 className="card-title text-muted mb-1">
                                <i className="fas fa-unlink me-1"></i>
                                Cinza Piscando - Sem Vínculo
                              </h6>
                              <p className="card-text small mb-0">
                                ⚠️ Professor não tem vínculo com esta turma (disciplina não cadastrada). 
                                <strong>Ações de salvar e imprimir ficam bloqueadas até resolver este problema.</strong>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 mt-4">
                       <div className="alert alert-info d-flex align-items-start">
                         <div>
                           <h6 className="alert-heading mb-2 d-flex align-items-center" style={{fontSize: '1.1rem'}}>
                              <i className="fas fa-info-circle me-2"></i>
                              Dicas de Uso:
                            </h6>
                            <div style={{textAlign: 'left', fontSize: '0.9rem'}}>
                              Passe o mouse sobre qualquer card para ver detalhes dos conflitos<br/>
                              Arraste e solte professores entre os horários para reorganizar
                            </div>
                         </div>
                       </div>
                     </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowManualModal(false)}
                  >
                    <i className="fas fa-times me-2"></i>
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      <AlertModal
        isOpen={showAlert}
        title="Erro na Geração da Grade"
        message="Não foi possível gerar uma grade parcial, as possíveis causas são professores com pouca agenda, verifique e libere mais agenda de professores e tente novamente."
        onClose={() => setShowAlert(false)}
      />

      {grade && (
        <SubstituirProfessorModal
          isOpen={showSubstituirProfessorModal}
          onClose={() => setShowSubstituirProfessorModal(false)}
          gradeId={grade.id!}
          horarios={grade.horarios ?? []}
          professores={professores}
          onSucesso={() => loadGrade(true)}
        />
      )}

      {resultadoLabModal.show && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className={`modal-header ${resultadoLabModal.erro ? 'bg-danger text-white' : 'bg-primary text-white'}`}>
                <h5 className="modal-title">
                  <i className={`fas ${resultadoLabModal.erro ? 'fa-exclamation-triangle' : 'fa-flask'} me-2`}></i>
                  {resultadoLabModal.erro
                    ? 'Não foi possível gerar a grade de laboratório'
                    : resultadoLabModal.diagnostico.length > 0
                      ? 'Grade de laboratório gerada com avisos'
                      : 'Grade de laboratório gerada com sucesso!'}
                </h5>
              </div>
              <div className="modal-body">
                {resultadoLabModal.erro ? (
                  <p className="mb-0">{resultadoLabModal.erro}</p>
                ) : (
                  <>
                    {resultadoLabModal.diagnostico.length > 0 ? (
                      <>
                        <p>Algumas aulas não conseguiram laboratório nesta geração:</p>
                        <ul className="mb-3">
                          {resultadoLabModal.diagnostico.map((item, i) => (
                            <li key={i}>{item.mensagem}</li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <p>A grade de laboratório foi gerada sem nenhum conflito.</p>
                    )}

                    {resultadoLabModal.sugestoesRemanejamento.length > 0 && (
                      <div className="mt-3">
                        <h6 className="fw-bold">
                          <i className="fas fa-route me-2"></i>
                          Sugestões para resolver o que sobrou
                        </h6>
                        <p className="text-muted small mb-2">
                          Marque as trocas que quer aplicar na Grade principal. Nada é salvo até você confirmar.
                        </p>
                        {resultadoLabModal.sugestoesRemanejamento.map((sugestao, i) => {
                          const id = sugestao.aulaSemLaboratorio.gradeHorarioId;
                          if (sugestao.tipo === 'sem_solucao') {
                            return (
                              <div key={i} className="alert alert-secondary py-2 mb-2">
                                <i className="fas fa-ban me-2"></i>
                                {sugestao.mensagem}
                              </div>
                            );
                          }
                          const pronta = sugestao.tipo === 'troca_pronta';
                          return (
                            <div key={i} className={`alert ${pronta ? 'alert-info' : 'alert-warning'} py-2 mb-2`}>
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`sugestao-${i}`}
                                  checked={resultadoLabModal.aceitas.has(id)}
                                  onChange={() => handleToggleAceitarSugestao(id)}
                                  disabled={aplicandoRemanejamento}
                                />
                                <label className="form-check-label" htmlFor={`sugestao-${i}`}>
                                  {sugestao.mensagem}
                                </label>
                              </div>
                              {!pronta && sugestao.bloqueios && sugestao.bloqueios.length > 0 && (
                                <div className="small text-muted mt-1 ms-4">
                                  <i className="fas fa-exclamation-circle me-1"></i>
                                  Depende de liberar: {sugestao.bloqueios.map((b) => `${b.nome} (${b.horario})`).join('; ')}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setResultadoLabModal({ show: false, gradeLaboratorioId: null, diagnostico: [], sugestoesRemanejamento: [], aceitas: new Set(), erro: null })}
                  disabled={aplicandoRemanejamento}
                >
                  Fechar
                </button>
                {resultadoLabModal.aceitas.size > 0 && (
                  <button
                    type="button"
                    className="btn btn-warning"
                    onClick={handleAplicarRemanejamento}
                    disabled={aplicandoRemanejamento}
                  >
                    {aplicandoRemanejamento ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Aplicando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check me-2"></i>
                        Aplicar {resultadoLabModal.aceitas.size} troca(s) e recalcular
                      </>
                    )}
                  </button>
                )}
                {resultadoLabModal.gradeLaboratorioId && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => navigate(`/grade-laboratorio/${resultadoLabModal.gradeLaboratorioId}`)}
                    disabled={aplicandoRemanejamento}
                  >
                    <i className="fas fa-eye me-2"></i>
                    Ver Grade de Laboratório
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default GradeViewPage;